import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { HardHat, Plus, RefreshCw, Building, CheckCircle, AlertTriangle, Clock, Shield, FileText, Star, TrendingUp, Users, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractorManagementPage = () => {
  const [contractors, setContractors] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    services: [],
    years_in_business: null,
    employee_count: null,
    safety_program_description: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    fetchServices();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contractorsRes, statsRes, expiringRes] = await Promise.all([
        axios.get(`${API}/contractors`),
        axios.get(`${API}/contractors/stats/summary`),
        axios.get(`${API}/contractors/expiring?days=30`).catch(() => ({ data: { contractors: [] } }))
      ]);
      setContractors(contractorsRes.data.contractors || []);
      setStats(statsRes.data);
      setExpiring(expiringRes.data.contractors || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load contractors. Please refresh.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/contractors/services/list`);
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleCreateContractor = async () => {
    if (!formData.company_name || !formData.contact_name || !formData.contact_email || !formData.contact_phone) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    try {
      const response = await axios.post(`${API}/contractors`, formData);
      toast({ title: 'Success', description: 'Contractor submitted for pre-qualification!' });
      setShowCreateDialog(false);
      resetForm();
      // Force refresh after short delay
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (error) {
      console.error('Error creating contractor:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create contractor', variant: 'destructive' });
    }
  };

  const handleApprove = async (contractorId) => {
    try {
      const response = await axios.post(`${API}/contractors/${contractorId}/approve`);
      if (response.data.status === 'approved') {
        toast({ title: 'Success', description: 'Contractor approved!' });
      } else {
        toast({ title: 'Warning', description: `Cannot approve: Missing ${response.data.missing?.join(', ')}`, variant: 'destructive' });
      }
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve contractor', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      services: [],
      years_in_business: null,
      employee_count: null,
      safety_program_description: ''
    });
  };

  const toggleService = (service) => {
    const current = formData.services;
    if (current.includes(service)) {
      setFormData({...formData, services: current.filter(s => s !== service)});
    } else {
      setFormData({...formData, services: [...current, service]});
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      conditional: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800',
      expired: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const filteredContractors = contractors.filter(c => {
    const matchesSearch = c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Loading contractors...</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <HardHat className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Contractor Management</h1>
              <p className="text-sm text-slate-300">Pre-qualification, insurance & safety compliance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Contractor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Contractor</DialogTitle>
                  <DialogDescription>Submit contractor for pre-qualification review</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Company Name *</Label>
                      <Input value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} placeholder="Company name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Name *</Label>
                      <Input value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} placeholder="Contact person" />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email *</Label>
                      <Input type="email" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} placeholder="email@company.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Phone *</Label>
                      <Input value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} placeholder="(555) 555-5555" />
                    </div>
                    <div className="space-y-2">
                      <Label>Years in Business</Label>
                      <Input type="number" value={formData.years_in_business || ''} onChange={e => setFormData({...formData, years_in_business: parseInt(e.target.value) || null})} placeholder="Years" />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Address</Label>
                      <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Street address" />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="City" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="ST" />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP</Label>
                        <Input value={formData.zip_code} onChange={e => setFormData({...formData, zip_code: e.target.value})} placeholder="ZIP" />
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Services Provided</Label>
                      <div className="flex flex-wrap gap-2">
                        {services.map(s => (
                          <Badge key={s} variant={formData.services.includes(s) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleService(s)}>
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Safety Program Description</Label>
                      <Textarea value={formData.safety_program_description} onChange={e => setFormData({...formData, safety_program_description: e.target.value})} placeholder="Describe your safety program" rows={3} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateContractor}>Submit for Pre-Qualification</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-2xl font-bold">{stats.total_contractors}</p>
                </div>
                <Building className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_status?.approved || 0}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.pending || 0}</p>
                </div>
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={expiring.length > 0 ? 'border-orange-200 bg-orange-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Expiring Soon</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.expiring_insurance_30_days || expiring.length}</p>
                </div>
                <AlertTriangle className="w-6 h-6 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Excellent (90+)</p>
                  <p className="text-2xl font-bold">{stats.score_distribution?.excellent || 0}</p>
                </div>
                <Star className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input className="pl-10" placeholder="Search contractors..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="conditional">Conditional</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Expiring Warning */}
      {expiring.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Insurance Expiring Soon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {expiring.slice(0, 4).map(c => (
                <div key={c.id} className="p-3 bg-white rounded-lg border">
                  <p className="font-medium">{c.company_name}</p>
                  {c.expiring_items?.map((item, i) => (
                    <p key={i} className="text-sm text-orange-600">
                      {item.item}: {item.days_remaining} days remaining
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contractors List */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Contractors</TabsTrigger>
          <TabsTrigger value="pending">Pending Review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {filteredContractors.map(contractor => (
            <Card key={contractor.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{contractor.company_name}</h3>
                        <Badge className={getStatusColor(contractor.status)}>{contractor.status}</Badge>
                        {contractor.has_expiring && <Badge variant="outline" className="text-orange-600 border-orange-300">Expiring</Badge>}
                      </div>
                      <p className="text-sm text-gray-500">{contractor.contact_name} • {contractor.contact_email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {contractor.services?.slice(0, 3).map(s => (
                          <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                        ))}
                        {contractor.services?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{contractor.services.length - 3}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Safety Score</p>
                      <p className={`text-2xl font-bold ${getScoreColor(contractor.safety_score)}`}>
                        {contractor.safety_score || '-'}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {contractor.status === 'pending' && (
                        <Button size="sm" onClick={() => handleApprove(contractor.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setSelectedContractor(contractor)}>
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredContractors.length === 0 && !loading && (
            <Card>
              <CardContent className="p-12 text-center">
                <HardHat className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No contractors found</h3>
                <p className="text-gray-500 mb-4">Add contractors for pre-qualification</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Contractor
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Contractor Detail Dialog */}
      <Dialog open={!!selectedContractor} onOpenChange={() => setSelectedContractor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedContractor && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{selectedContractor.company_name}</DialogTitle>
                  <Badge className={getStatusColor(selectedContractor.status)}>
                    {selectedContractor.status}
                  </Badge>
                </div>
              </DialogHeader>
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-center">
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500 mb-1">Safety Score</p>
                    <p className={`text-5xl font-bold ${getScoreColor(selectedContractor.safety_score)}`}>
                      {selectedContractor.safety_score || 0}
                    </p>
                    <Progress value={selectedContractor.safety_score || 0} className="mt-2 w-32" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">{selectedContractor.contact_name}</p>
                    <p className="text-sm">{selectedContractor.contact_email}</p>
                    <p className="text-sm">{selectedContractor.contact_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p>{selectedContractor.address}</p>
                    <p>{selectedContractor.city}, {selectedContractor.state} {selectedContractor.zip_code}</p>
                  </div>
                </div>

                {selectedContractor.services?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Services</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedContractor.services.map(s => (
                        <Badge key={s} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedContractor.expiring_items?.length > 0 && (
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <p className="font-medium text-orange-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Expiring Items
                    </p>
                    {selectedContractor.expiring_items.map((item, i) => (
                      <p key={i} className="text-sm text-orange-600">
                        {item.type}: {item.item} - {item.days_remaining} days remaining
                      </p>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  {selectedContractor.status === 'pending' && (
                    <Button className="flex-1" onClick={() => { handleApprove(selectedContractor.id); setSelectedContractor(null); }}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve Contractor
                    </Button>
                  )}
                  <Button variant="outline" className="flex-1" onClick={() => setSelectedContractor(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorManagementPage;
